import React, { useCallback, useState } from 'react';

// Small shared helpers for sortable admin tables (ported from the Goose
// user-management page). `sort` is always { key, dir } with dir 'asc' | 'desc'.

export function nextSort(prev, key) {
    if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    }
    return { key, dir: 'asc' };
}

export function useClientSort(initial = { key: null, dir: 'asc' }) {
    const [sort, setSort] = useState(initial);
    const onSort = useCallback((key) => setSort(prev => nextSort(prev, key)), []);
    return [sort, onSort];
}

// A clickable table header that shows the current sort direction.
export function SortableTh({ label, sortKey, sort, onSort, className }) {
    const active = sort.key === sortKey;
    const arrow = active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '';
    const classes = ['sortable-th'];
    if (active) classes.push('active');
    if (className) classes.push(className);
    return (
        <th
            className={classes.join(' ')}
            onClick={() => onSort(sortKey)}
            title='Click to sort'
        >
            {label}<span className='sort-arrow'>{arrow}</span>
        </th>
    );
}

const isEmpty = v => v === null || v === undefined || v === '';

// Returns a new sorted array. `accessors` maps a sort key to a function that
// extracts the comparable value for a row (defaults to row[key]). Empty values
// always sort to the bottom, regardless of the sort direction.
export function sortRows(rows, sort, accessors = {}) {
    if (!sort.key) return rows;
    const accessor = accessors[sort.key] || (row => row[sort.key]);
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const av = accessor(a);
        const bv = accessor(b);
        if (isEmpty(av) && isEmpty(bv)) return 0;
        if (isEmpty(av)) return 1;
        if (isEmpty(bv)) return -1;
        let cmp;
        if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
        else cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
        return cmp * dir;
    });
}
